import React, {forwardRef, ReactElement, useEffect, useRef} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Backdrop,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import './ArrModal.scss';
import {Transition} from 'react-transition-group';
import ArrButton from '../arr-button/ArrButton';
import {TransitionProps} from 'react-transition-group/Transition';
import {isEmpty} from 'lodash';

export interface ModalButton {
  id: string;
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'text';
  size?: 'small' | 'medium' | 'large' | 'xs';
  disabled?: boolean;
  dataTestId: string;
  loading?: boolean;
  className?: string;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  ariaDisabled?: 'true' | 'false';
}

export interface ArrModalProps {
  showModal: boolean;
  headerTitle?: string;
  headerTitleElement?: ReactElement;
  title?: string;
  heading?: string;
  titleStartIcon?: React.ReactNode;
  titleEndIcon?: React.ReactNode;
  description?: string | ReactElement;
  footer?: ReactElement;
  showClose?: boolean;
  disabledClose?: boolean;
  onClose: () => void;
  buttons?: ModalButton[];
  children?: React.ReactNode;
  className?: string;
  dataTestId: string;
  headerTitleClassName?: string;
  ariaLabel?: string;
  ariaDescription?: string;
  hideDefaultActions?: boolean; // To hide empty space of actions without button
  autoFocusCloseButton?: boolean; // To focus on close button when modal opens
  descriptionHasHtml?: boolean; //Description is  html content
  disablePortal?: boolean; // Optional prop to control portal behavior
  disableFocusTitle ?: boolean;
}

type WaterfallTransitionProps = TransitionProps & {
  children: ReactElement;
};

const WaterfallTransition = forwardRef<
  HTMLDivElement,
  WaterfallTransitionProps
>(({in: inProp, children, ...rest}, ref) => {
  const nodeRef = useRef<HTMLDivElement>(ref as any);

  return (
    <Transition
      nodeRef={nodeRef}
      in={inProp}
      timeout={300}
      mountOnEnter
      unmountOnExit
      {...rest}
    >
      {(state: string) =>
        React.cloneElement(children, {
          ref: nodeRef,
          className: `waterfall-${state} ${children.props.className || ''}`,
        })
      }
    </Transition>
  );
});

const ArrModal: React.FC<ArrModalProps> = ({
  showModal,
  headerTitle,
  headerTitleElement,
  title,
  titleStartIcon,
  titleEndIcon,
  heading,
  description,
  footer,
  showClose = true,
  disabledClose = false,
  onClose,
  buttons = [],
  children,
  className,
  headerTitleClassName = 'label2_size',
  dataTestId,
  ariaLabel,
  ariaDescription,
  hideDefaultActions = false,
  autoFocusCloseButton = false,
  descriptionHasHtml = false,
  disablePortal = false, // ✅ Default to false to maintain existing behavior
  disableFocusTitle,
  ...restModalProps
}) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (showModal) {
      previouslyFocusedElement.current =
        (document.activeElement as HTMLElement) || null;
    } else if (!showModal && previouslyFocusedElement.current) {
      previouslyFocusedElement.current.focus();
    }
  }, [showModal]);

  useEffect(() => {
    if (!showModal) return;

    const timer = setTimeout(() => {
      if (autoFocusCloseButton && closeButtonRef.current) {
        // Explicitly requested: start on close button
        closeButtonRef.current.focus();
      } else if (dialogRef.current) {
        // Default: focus the dialog container so first Tab goes to topmost control
        dialogRef.current.focus();
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [autoFocusCloseButton, showModal]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;

    const focusable = Array.from(
      dialogRef.current.querySelectorAll<HTMLElement>(
        'button, a[href], input, select, textarea, [role="button"], [role="link"], [role="checkbox"], [role="radio"], [role="switch"],[tabindex]:not([tabindex="-1"])'
      )
    );

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;
    const isInsideTrap = !!active && focusable.includes(active);

    const targetFromEdge = e.shiftKey ? last : first;

    // If focus is on the dialog container or outside the trap,
    // move directly to first/last interactive element.
    if (!isInsideTrap || active === dialogRef.current) {
      e.preventDefault();
      targetFromEdge.focus();
      return;
    }

    if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    }
  };

  const renderHeaderTitle = () => {
    if (headerTitleElement) return headerTitleElement;
    else {
      return headerTitle ? (
        <Typography
          className={`typo-text ${headerTitleClassName} custom-text-color `}
          data-testid={`modal-header-title`}
          tabIndex={0}
        >
          {headerTitle}
        </Typography>
      ) : (
        <span data-testid={`arr-modal-span`} />
      );
    }
  };

  const modalContent = (
    <Dialog
      open={showModal}
      onClose={(
        _: React.SyntheticEvent,
        reason: 'backdropClick' | 'escapeKeyDown'
      ) => {
        if ((reason !== 'backdropClick' && disabledClose) || !disabledClose) {
          onClose?.();
        }
      }}
      className={`arr-modal ${className}`}
      keepMounted={false}
      data-testid={dataTestId}
      aria-labelledby={`${dataTestId}-title`}
      aria-describedby={`${dataTestId}-description`}
      slots={{backdrop: Backdrop, transition: WaterfallTransition}}
      slotProps={{
        transition: {timeout: 300},
        backdrop: {className: 'modal-backdrop'},
        paper: {className: 'modal-paper'},
      }}
      disablePortal={disablePortal}
      tabIndex={-1}
      ref={dialogRef}
      onKeyDown={handleKeyDown}
      {...restModalProps}
    >
      {/* Header */}
      <DialogTitle
        id={`${dataTestId}-header-title`}
        className="modal-header-title"
      >
        {renderHeaderTitle()}
        {showClose && (
          <IconButton
            aria-label="close dialog"
            size="small"
            onClick={onClose}
            className="modal-close"
            sx={{
              opacity: disabledClose ? 0.5 : 1,
              pointerEvents: disabledClose ? 'none' : 'auto',
            }}
            ref={closeButtonRef}
            data-testid={`${dataTestId}-close`}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>

      {/* Content */}
      <DialogContent className="modal-content">
        {title && (
          <Box className="modal-title-row">
            {titleStartIcon}
            <Typography
              id={`${dataTestId}-title`}
              className="typo-text headline3_size title-text-color"
              data-testid={`${dataTestId}-title`}
               tabIndex={disableFocusTitle ? -1 : 0}
            >
              {title}
            </Typography>
            {titleEndIcon}
          </Box>
        )}
        {description &&
          (descriptionHasHtml ? (
            <Typography
              id={`${dataTestId}-description`}
              className="typo-text body2_size desc-text-color"
              data-testid={`${dataTestId}-description`}
              dangerouslySetInnerHTML={{__html: description}}
            />
          ) : (
            <Typography
              id={`${dataTestId}-description`}
              className="typo-text body2_size desc-text-color"
              data-testid={`${dataTestId}-description`}
            >
              {description}
            </Typography>
          ))}
        {heading && (
          <Typography
            id={`${dataTestId}-heading`}
            className="typo-text body2_size desc-text-color"
            data-testid={`${dataTestId}-heading`}
          >
            {heading}
          </Typography>
        )}
        {children}
      </DialogContent>

      {/* Actions */}
      {!hideDefaultActions && (
        <DialogActions className="modal-actions">
          <Box className="button-row">
            {buttons.map((btn) => (
              <ArrButton
                key={btn.id}
                variant={btn.variant}
                onClick={btn.onClick}
                size={btn.size}
                disabled={btn.disabled}
                dataTestId={btn.dataTestId}
                loading={btn.loading}
                className={btn?.className}
                aria-label={btn.ariaLabel}
                aria-describedby={btn.ariaDescribedBy}
                aria-disabled={btn.ariaDisabled}
                isAriaDisabled={btn.ariaDisabled === 'true'}
              >
                {btn.label}
              </ArrButton>
            ))}
          </Box>
        </DialogActions>
      )}
      {!isEmpty(footer) && <>{footer}</>}
    </Dialog>
  );

  // ✅ Render directly in the current document (no portal detection needed)
  return modalContent;
};

export default ArrModal;
